import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Button, Modal, TextField, Tooltip, Chip,
  Select, MenuItem, Container, Divider, Paper, Snackbar, Alert
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Autocomplete from "@mui/material/Autocomplete";
import api from "../../services/api";
import { useTranslation } from "react-i18next";

const countryMap = {
  1: { label: "🇬🇷 GR", color: "success" },
  2: { label: "🇺🇸 USA", color: "primary" }
};

export default function ProductDashboard() {
  const { i18n } = useTranslation();

  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [generics, setGenerics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [editProduct, setEditProduct] = useState(null);
  const [mappingGeneric, setMappingGeneric] = useState(null);
  const [missingModalOpen, setMissingModalOpen] = useState(false);

  // Filters
  const [countryFilter, setCountryFilter] = useState(1);
  const [genericFilter, setGenericFilter] = useState("");
  const [missingSearch, setMissingSearch] = useState("");
  const [selectedProductToLink, setSelectedProductToLink] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const extractItems = (resp) =>
        Array.isArray(resp?.data)
          ? resp.data
          : resp?.data?.data || [];

      const [prods, vars, gens] = await Promise.all([
        api.products.list(),
        api.variants.list(),
        api.genericProducts.list()
      ]);
      setProducts(extractItems(prods));
      setVariants(extractItems(vars));
      setGenerics(extractItems(gens));
    } catch (err) {
      console.error("Dashboard load error:", err);
      setSnackbar({ open: true, message: "Failed to load data", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all (προϊόντα, παραλλαγές, γενικά προϊόντα)
  useEffect(() => {
    loadAll();
  }, [loadAll]);


  // Multilanguage Generic Name
  const getGenericName = (gp_id) => {
    if (!gp_id || !Array.isArray(generics)) return "-";
    const match = generics.find((g) => Number(g.id) === Number(gp_id));
    if (!match) return "-";
    return i18n.language === "el"
      ? (match.name_el ?? match.names?.el ?? match.name ?? "-")
      : (match.name_en ?? match.names?.en ?? match.name ?? "-");
  };

  const selectedCountryId = Number(countryFilter) || 1;

  const productsByCountry = useMemo(() => {
    return products.filter((p) => Number(p.country_origin) === Number(selectedCountryId));
  }, [products, selectedCountryId]);

  const productsByGenericInCountry = useMemo(() => {
    const map = new Map();
    for (const p of productsByCountry) {
      const gpId = Number(p.generic_product_id || 0);
      if (!gpId) continue;
      map.set(gpId, (map.get(gpId) || 0) + 1);
    }
    return map;
  }, [productsByCountry]);

  const allProductsByGeneric = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const gpId = Number(p.generic_product_id || 0);
      if (!gpId) continue;
      map.set(gpId, (map.get(gpId) || 0) + 1);
    }
    return map;
  }, [products]);

  const missingGenerics = useMemo(() => {
    return generics.filter((gp) => !productsByGenericInCountry.get(Number(gp.id)));
  }, [generics, productsByGenericInCountry]);

  const filteredGenerics = useMemo(() => {
    return generics.filter((g) =>
      genericFilter ? Number(g.id) === Number(genericFilter) : true
    );
  }, [generics, genericFilter]);

  const headerWithTooltip = (label, help) => (
    <Tooltip title={help} arrow>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}
      </Box>
    </Tooltip>
  );

  // DataGrid Columns (Generic-focused)
  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
      renderHeader: () => headerWithTooltip("ID", "Generic product primary key (generic_product.id).")
    },
    {
      field: "name",
      headerName: "Generic Name",
      width: 260,
      valueGetter: (params) => getGenericName(params.row.id),
      renderHeader: () => headerWithTooltip("Generic Name", "Resolved name from generic_product.name_* fields."),
      renderCell: (params) => (
        <Box sx={{ fontWeight: 600 }}>
          {getGenericName(params.row.id)}
        </Box>
      )
    },
    {
      field: "mapped_in_country",
      headerName: "Mapped (Country)",
      width: 160,
      renderHeader: () =>
        headerWithTooltip(
          "Mapped (Country)",
          "Count of products where product.generic_product_id == generic_product.id AND product.country_origin == selected country."
        ),
      valueGetter: (params) => productsByGenericInCountry.get(Number(params.row.id)) || 0,
      renderCell: (params) => (
        <Chip
          label={`${productsByGenericInCountry.get(Number(params.row.id)) || 0} product(s)`}
          color={(productsByGenericInCountry.get(Number(params.row.id)) || 0) > 0 ? "success" : "warning"}
          size="small"
        />
      )
    },
    {
      field: "mapped_total",
      headerName: "Mapped (All)",
      width: 140,
      renderHeader: () =>
        headerWithTooltip(
          "Mapped (All)",
          "Total count of products linked to this generic (any country)."
        ),
      valueGetter: (params) => allProductsByGeneric.get(Number(params.row.id)) || 0,
      renderCell: (params) => (
        <Chip
          label={`${allProductsByGeneric.get(Number(params.row.id)) || 0}`}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: "coverage",
      headerName: "Coverage",
      width: 150,
      renderHeader: () =>
        headerWithTooltip(
          "Coverage",
          "Status for selected country: ✅ mapped if there is at least one product in that country."
        ),
      renderCell: (params) => {
        const has = (productsByGenericInCountry.get(Number(params.row.id)) || 0) > 0;
        return (
          <Chip
            label={has ? "✅ Mapped" : "❌ Missing"}
            color={has ? "success" : "error"}
            size="small"
          />
        );
      }
    },
    {
      field: "actions",
      headerName: "Fix Mapping",
      width: 150,
      renderHeader: () =>
        headerWithTooltip(
          "Fix Mapping",
          "Link an existing product from the selected country to this generic."
        ),
      renderCell: (params) => {
        const has = (productsByGenericInCountry.get(Number(params.row.id)) || 0) > 0;
        return (
          <Button
            variant={has ? "outlined" : "contained"}
            color={has ? "primary" : "warning"}
            size="small"
            onClick={() => {
              setSelectedProductToLink(null);
              setMappingGeneric(params.row);
            }}
          >
            Fix
          </Button>
        );
      },
      sortable: false,
      filterable: false
    }
  ];

  return (
    <Container sx={{ mt: 4, mb: 10 }}>
      <Typography variant="h4" gutterBottom>
        Προϊόντα / Παραλλαγές / Generic Management
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Overview */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Primary Country
            </Typography>
            <Select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={1}>Ελλάδα (GR)</MenuItem>
              <MenuItem value={2}>USA</MenuItem>
            </Select>
            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
              All coverage metrics & missing counts are computed for this country only.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Paper
              onClick={() => setMissingModalOpen(true)}
              sx={{
                p: 2,
                minWidth: 220,
                cursor: "pointer",
                border: "2px solid",
                borderColor: "warning.main",
                bgcolor: "warning.light",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Missing mappings (selected country)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {missingGenerics.length}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Click to review & fix
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, minWidth: 180 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Total generics
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {generics.length}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                generic_product table
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, minWidth: 200 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Products in country
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {productsByCountry.length}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                product.country_origin = {selectedCountryId}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Paper>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Select
          value={genericFilter}
          onChange={(e) => setGenericFilter(e.target.value)}
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Generic: Όλα</MenuItem>
          {Array.isArray(generics) && generics.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {getGenericName(g.id)}
            </MenuItem>
          ))}
        </Select>

        <Button
          variant="outlined"
          startIcon={<AddCircleIcon />}
          onClick={() => window.location.href = "/admin/add-product"}
        >
          Νέο προϊόν
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddCircleIcon />}
          onClick={() => window.location.href = "/admin/add-generic-product"}
        >
          Νέο generic
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddCircleIcon />}
          onClick={() => window.location.href = "/admin/add-variant-product"}
        >
          Νέα παραλλαγή
        </Button>
      </Box>

      <DataGrid
        rows={filteredGenerics}
        columns={columns}
        autoHeight
        loading={loading}
        pageSize={15}
        rowsPerPageOptions={[15, 30, 100]}
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          "& .MuiDataGrid-cell": { fontSize: "1rem" },
          "& .row-missing": {
            bgcolor: "rgba(245, 124, 0, 0.12)",
            fontWeight: 700
          },
          boxShadow: 2
        }}
        getRowId={(row) => row.id}
        getRowClassName={(params) =>
          (productsByGenericInCountry.get(Number(params.row.id)) || 0) > 0
            ? ""
            : "row-missing"
        }
      />

      {/* --- MISSING MAPPINGS MODAL --- */}
      <Modal open={missingModalOpen} onClose={() => setMissingModalOpen(false)}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          color: "text.primary",
          borderRadius: 2, boxShadow: 24, p: 4, minWidth: 520, maxHeight: "80vh", overflow: "auto"
        }}>
          <Typography variant="h6" gutterBottom>
            Missing mappings for {countryMap[selectedCountryId]?.label}
          </Typography>
          <TextField
            label="Search generic"
            value={missingSearch}
            onChange={(e) => setMissingSearch(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "grid", gap: 1 }}>
            {missingGenerics
              .filter((g) => {
                const name = getGenericName(g.id).toLowerCase();
                return !missingSearch || name.includes(missingSearch.toLowerCase());
              })
              .map((g) => (
                <Paper key={g.id} sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {getGenericName(g.id)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Generic ID: {g.id}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => {
                      setMissingModalOpen(false);
                      setSelectedProductToLink(null);
                      setMappingGeneric(g);
                    }}
                  >
                    Fix
                  </Button>
                </Paper>
              ))}
            {!missingGenerics.length && (
              <Typography variant="body2">All generics are mapped for this country.</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* --- MAPPING MODAL --- */}
      <Modal open={!!mappingGeneric} onClose={() => setMappingGeneric(null)}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          color: "text.primary",
          borderRadius: 2, boxShadow: 24, p: 4, minWidth: 520, maxHeight: "80vh", overflow: "auto"
        }}>
          {mappingGeneric && (
            <>
              <Typography variant="h6" gutterBottom>
                Fix mapping for: {getGenericName(mappingGeneric.id)}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                Generic ID: {mappingGeneric.id}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Linked products (selected country: {countryMap[selectedCountryId]?.label})
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                  {productsByCountry
                    .filter((p) => Number(p.generic_product_id) === Number(mappingGeneric.id))
                    .map((p) => (
                      <Chip
                        key={p.id}
                        label={`${p.name_en} (#${p.id})`}
                        color="success"
                        size="small"
                        clickable
                        onClick={() => setEditProduct(p)}
                      />
                    ))}
                  {!productsByCountry.some((p) => Number(p.generic_product_id) === Number(mappingGeneric.id)) && (
                    <Typography variant="caption">No linked products for this country.</Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Linked products (all countries)
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                  {products
                    .filter((p) => Number(p.generic_product_id) === Number(mappingGeneric.id))
                    .map((p) => (
                      <Chip
                        key={p.id}
                        label={`${p.name_en} (${countryMap[p.country_origin]?.label || p.country_origin})`}
                        size="small"
                        clickable
                        onClick={() => setEditProduct(p)}
                      />
                    ))}
                  {!products.some((p) => Number(p.generic_product_id) === Number(mappingGeneric.id)) && (
                    <Typography variant="caption">No linked products yet.</Typography>
                  )}
                </Box>
              </Box>

              <Autocomplete
                options={productsByCountry.filter(
                  (p) => Number(p.generic_product_id) !== Number(mappingGeneric.id)
                )}
                value={selectedProductToLink}
                onChange={(_, newVal) => setSelectedProductToLink(newVal)}
                getOptionLabel={(p) =>
                  `${p.name_en || p.name_el || "Untitled"} (#${p.id})` +
                  (p.generic_product_id ? ` • linked to GP ${p.generic_product_id}` : "")
                }
                isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select existing product (selected country)"
                    helperText="Only products from the selected country are shown."
                  />
                )}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={!selectedProductToLink}
                  onClick={async () => {
                    if (!selectedProductToLink) return;
                    try {
                      await api.products.update(selectedProductToLink.id, {
                        generic_product_id: mappingGeneric.id
                      });
                      setSnackbar({ open: true, message: "Mapping updated", severity: "success" });
                      setMappingGeneric(null);
                      setSelectedProductToLink(null);
                      await loadAll();
                    } catch (err) {
                      setSnackbar({
                        open: true,
                        message: err.response?.data?.error || "Failed to update mapping",
                        severity: "error"
                      });
                    }
                  }}
                >
                  Save mapping
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => window.location.href = "/admin/add-product"}
                >
                  Create new product
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* EDIT PRODUCT MODAL */}
      <Modal open={!!editProduct} onClose={() => setEditProduct(null)}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          color: "text.primary",
          borderRadius: 2, boxShadow: 24, p: 4, minWidth: 400
        }}>
          {editProduct && (
            <>
              <Typography variant="h6" gutterBottom>
                Επεξεργασία Προϊόντος: {editProduct.name_en}
              </Typography>
              <TextField
                label="Όνομα (en)"
                value={editProduct.name_en}
                fullWidth
                sx={{ my: 1 }}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Όνομα (el)"
                value={editProduct.name_el}
                fullWidth
                sx={{ my: 1 }}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Μονάδα (en)"
                value={editProduct.unit_en}
                fullWidth
                sx={{ my: 1 }}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Generic"
                value={getGenericName(editProduct.generic_product_id)}
                fullWidth
                sx={{ my: 1 }}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Χώρα"
                value={countryMap[editProduct.country_origin]?.label || editProduct.country_origin}
                fullWidth
                sx={{ my: 1 }}
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => {
                  window.location.href = `/admin/update-product?id=${editProduct.id}`;
                }}>
                Άνοιγμα πλήρους φόρμας
              </Button>
            </>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
